import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space,
  Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  DeleteOutlined, EditOutlined, PlusOutlined, TrophyOutlined,
} from '@ant-design/icons';
import {
  createCompetition, createCompetitionOrganization, createCompetitionProject,
  deleteCompetition, deleteCompetitionOrganization, deleteCompetitionProject,
  getCompetitionOrganizations, getCompetitionProjects, getCompetitionYears,
  getCompetitions, updateCompetition, updateCompetitionOrganization,
  updateCompetitionProject,
} from '../api/competitions';
import { getBranches } from '../api/branches';
import useAuthStore from '../store/authStore';
import apiErrorMessage from '../utils/apiError';
import useServerTable from '../hooks/useServerTable';

const { Title, Text } = Typography;

const COMPETITION_STATUS = {
  planning:  { label: 'Planlama',   color: 'default' },
  active:    { label: 'Aktif',      color: 'processing' },
  completed: { label: 'Tamamlandı', color: 'success' },
  cancelled: { label: 'İptal',      color: 'error' },
};

const Competitions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('competitions');

  return (
    <div>
      <Title level={3}>
        <TrophyOutlined /> Yarışmalar
      </Title>
      <Text type="secondary">
        Organizasyon → Yarışma (yıl) → Etap yapısı. Bir yarışmanın etapları
        (İzmir, İstanbul, Roma…) yarışmanın detay sayfasında yönetilir.
      </Text>

      <Tabs
        style={{ marginTop: 16 }}
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'competitions',  label: 'Yarışmalar',    children: <CompetitionsTab navigate={navigate} /> },
          { key: 'organizations', label: 'Organizasyonlar', children: <OrganizationsTab /> },
          { key: 'projects',      label: 'Projeler',      children: <ProjectsTab /> },
        ]}
      />
    </div>
  );
};

// ---------------------------------------------------------------- Yarışmalar

const CompetitionsTab = ({ navigate }) => {
  const { user } = useAuthStore();
  // Yarisma bir SUBEYE ait. Super adminler sube secebilir; sube muduru kendi
  // subesine kilitli (backend zaten kisitliyor, burasi sadece gorunum).
  const canPickBranch = ['developer_admin', 'super_admin'].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // Sayfalama + sunucu sıralaması tek yerden (bkz. hooks/useServerTable).
  const table = useServerTable('competitions', 15);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);
  const [filters, setFilters] = useState({ year: null, organization_id: null, branch_id: null, status: null, search: '' });
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  const fetchRows = useCallback(async (override = filters) => {
    setLoading(true);
    try {
      const { data } = await getCompetitions({
        ...table.params(),
        year: override.year || undefined,
        organization_id: override.organization_id || undefined,
        branch_id: override.branch_id || undefined,
        status: override.status || undefined,
        search: override.search || undefined,
      });
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Yarışmalar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.page, table.pageSize, table.sorter, filters]);

  // Veri çekme yalnızca burada; useServerTable'ın onChange'i sadece state
  // güncelliyor, aksi halde her sıralamada iki istek giderdi.
  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    getCompetitionOrganizations({ per_page: 100, is_active: true })
      .then(({ data }) => setOrganizations(data.data || []))
      .catch(() => setOrganizations([]));
    getBranches({ per_page: 100 })
      .then(({ data }) => setBranches(data.data || data || []))
      .catch(() => setBranches([]));
    getCompetitionYears()
      .then(({ data }) => setYears(data || []))
      .catch(() => setYears([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilter = (patch) => {
    // Filtre degisince ilk sayfaya don - aksi halde kullanici daralan bir
    // listenin var olmayan 3. sayfasinda bos ekran goruyor. Cekme islemi
    // filters degisikligiyle useEffect'ten tetikleniyor.
    setFilters({ ...filters, ...patch });
    table.resetToFirstPage();
  };

  const openModal = (record = null) => {
    setModal({ open: true, record });
    form.setFieldsValue(record
      ? { ...record }
      : {
          year: new Date().getFullYear(),
          status: 'planning',
          // Sube muduru icin tek secenek zaten kendi subesi - pesin doldurup
          // gereksiz bir secim adimindan kurtariyoruz.
          branch_id: canPickBranch ? undefined : user?.branch_id,
        });
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (modal.record) {
        await updateCompetition(modal.record.id, values);
        message.success('Yarışma güncellendi.');
      } else {
        await createCompetition(values);
        message.success('Yarışma oluşturuldu.');
      }
      setModal({ open: false, record: null });
      form.resetFields();
      fetchRows();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  const remove = async (id) => {
    try {
      await deleteCompetition(id);
      message.success('Yarışma silindi.');
      fetchRows();
    } catch (e) {
      // 409: katilimci kaydi var - sunucunun acikladigi yonlendirmeyi goster
      message.warning(apiErrorMessage(e));
    }
  };

  // key alanlari backend'in beyaz listesiyle BIREBIR ayni olmali
  // (CompetitionController::index -> SortableQuery). Farkli yazilirsa kolon
  // tiklanabilir gorunur ama sunucu sessizce varsayilana duser.
  const columns = [
    {
      title: 'Yarışma',
      dataIndex: 'name',
      key: 'name',
      render: (v, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/competitions/${r.id}`)}>
          {v}
        </Button>
      ),
    },
    { title: 'Organizasyon', dataIndex: ['organization', 'name'], key: 'organization', render: (v) => v || '—' },
    // Sube sutunu yalnizca birden fazla subeyi gorebilen rollere anlamli;
    // sube muduru zaten sadece kendi subesini goruyor.
    ...(canPickBranch
      ? [{ title: 'Şube', dataIndex: ['branch', 'name'], key: 'branch', width: 140, render: (v) => v || <Text type="secondary">—</Text> }]
      : []),
    // Genişlikler sıralama okunu da hesaba katıyor: 80px'te başlık metni
    // okla birlikte sığmayıp alt satıra kayıyordu ("Takım" -> "Takı / m").
    { title: 'Yıl', dataIndex: 'year', key: 'year', width: 100 },
    { title: 'Etap', dataIndex: 'stages_count', key: 'stages_count', width: 110, render: (v) => v ?? 0 },
    { title: 'Takım', dataIndex: 'teams_count', key: 'teams_count', width: 115, render: (v) => v ?? 0 },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v) => {
        const s = COMPETITION_STATUS[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'İşlemler',
      width: 110,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Tooltip title="Düzenle">
            <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)} />
          </Tooltip>
          <Popconfirm title="Yarışma silinsin mi?" onConfirm={() => remove(r.id)} okText="Sil" cancelText="Vazgeç">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Yarışma ara"
          allowClear
          style={{ width: 220 }}
          onSearch={(v) => applyFilter({ search: v })}
        />
        <Select
          placeholder="Yıl"
          allowClear
          style={{ width: 120 }}
          value={filters.year}
          onChange={(v) => applyFilter({ year: v })}
          options={years.map((y) => ({ value: y, label: y }))}
        />
        <Select
          placeholder="Organizasyon"
          allowClear
          style={{ width: 200 }}
          value={filters.organization_id}
          onChange={(v) => applyFilter({ organization_id: v })}
          options={organizations.map((o) => ({ value: o.id, label: o.name }))}
        />
        {/* Sube filtresi yalnizca birden fazla subeyi gorebilenlere anlamli */}
        {canPickBranch && (
          <Select
            placeholder="Şube"
            allowClear
            style={{ width: 180 }}
            value={filters.branch_id}
            onChange={(v) => applyFilter({ branch_id: v })}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
          />
        )}
        <Select
          placeholder="Durum"
          allowClear
          style={{ width: 150 }}
          value={filters.status}
          onChange={(v) => applyFilter({ status: v })}
          options={Object.entries(COMPETITION_STATUS).map(([k, v]) => ({ value: k, label: v.label }))}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          Yeni Yarışma
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns.map((c) => ({ ...c, ...(c.key ? table.sortProps(c.key) : {}) }))}
        dataSource={rows}
        scroll={{ x: 800 }}
        {...table.tableProps(total, { showTotal: (t) => `${t} yarışma` })}
      />

      <Modal
        title={modal.record ? 'Yarışmayı Düzenle' : 'Yeni Yarışma'}
        open={modal.open}
        onOk={submit}
        onCancel={() => { setModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="competition_organization_id"
            label="Organizasyon"
            rules={[{ required: true, message: 'Organizasyon seçin.' }]}
          >
            <Select
              placeholder="Fibonacci, FLL Explore, Robotex…"
              options={organizations.map((o) => ({ value: o.id, label: o.name }))}
            />
          </Form.Item>
          <Form.Item
            name="branch_id"
            label="Şube"
            rules={[{ required: true, message: 'Şube seçin.' }]}
            extra={canPickBranch
              ? 'Yarışmayı düzenleyen şube. Katılımcılar, ücretler ve belgeler bu şubeye bağlı olur.'
              : 'Kendi şubeniz otomatik seçildi.'}
          >
            <Select
              placeholder="Şube seçin"
              disabled={!canPickBranch}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
            />
          </Form.Item>
          <Form.Item name="name" label="Yarışma Adı" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="Fibonacci 2026" />
          </Form.Item>
          <Form.Item name="year" label="Yıl" rules={[{ required: true, message: 'Yıl girin.' }]}>
            <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Durum">
            <Select options={Object.entries(COMPETITION_STATUS).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ----------------------------------------------------------- Organizasyonlar

const OrganizationsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCompetitionOrganizations({ per_page: 100 });
      setRows(data.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Organizasyonlar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (modal.record) await updateCompetitionOrganization(modal.record.id, values);
      else await createCompetitionOrganization(values);
      message.success('Kaydedildi.');
      setModal({ open: false, record: null });
      form.resetFields();
      fetchRows();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  return (
    <Card>
      <Space style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setModal({ open: true, record: null }); form.setFieldsValue({ is_active: true }); }}
        >
          Yeni Organizasyon
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'Organizasyon', dataIndex: 'name' },
          { title: 'Yarışma Sayısı', dataIndex: 'competitions_count', width: 130, render: (v) => v ?? 0 },
          {
            title: 'Durum',
            dataIndex: 'is_active',
            width: 100,
            render: (v) => <Tag color={v ? 'success' : 'default'}>{v ? 'Aktif' : 'Pasif'}</Tag>,
          },
          {
            title: 'İşlemler',
            width: 110,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { setModal({ open: true, record: r }); form.setFieldsValue(r); }}
                />
                <Popconfirm
                  title="Organizasyon silinsin mi?"
                  onConfirm={async () => {
                    try {
                      await deleteCompetitionOrganization(r.id);
                      message.success('Silindi.');
                      fetchRows();
                    } catch (e) {
                      message.warning(apiErrorMessage(e));
                    }
                  }}
                  okText="Sil"
                  cancelText="Vazgeç"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={modal.record ? 'Organizasyonu Düzenle' : 'Yeni Organizasyon'}
        open={modal.open}
        onOk={submit}
        onCancel={() => { setModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="Fibonacci" />
          </Form.Item>
          <Form.Item name="website" label="Web Sitesi">
            <Input placeholder="https://…" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="Durum">
            <Select options={[{ value: true, label: 'Aktif' }, { value: false, label: 'Pasif' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ----------------------------------------------------------------- Projeler

const ProjectsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  const fetchRows = useCallback(async (term = search) => {
    setLoading(true);
    try {
      const { data } = await getCompetitionProjects({ per_page: 100, search: term || undefined });
      setRows(data.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Projeler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchRows(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (modal.record) await updateCompetitionProject(modal.record.id, values);
      else await createCompetitionProject(values);
      message.success('Kaydedildi.');
      setModal({ open: false, record: null });
      form.resetFields();
      fetchRows();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  return (
    <Card>
      <Text type="secondary">
        Proje havuzu yarışmadan bağımsızdır — aynı proje birden fazla yarışmaya,
        farklı kategorilerle girebilir.
      </Text>
      <Space wrap style={{ margin: '12px 0' }}>
        <Input.Search
          placeholder="Proje ara"
          allowClear
          style={{ width: 240 }}
          onSearch={(v) => { setSearch(v); fetchRows(v); }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setModal({ open: true, record: null }); form.setFieldsValue({ is_active: true }); }}
        >
          Yeni Proje
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 20, showTotal: (t) => `${t} proje` }}
        columns={[
          { title: 'Proje', dataIndex: 'name' },
          { title: 'Teknik Detay', dataIndex: 'tech_details', render: (v) => v || '—' },
          {
            title: 'Tahmini Maliyet',
            dataIndex: 'estimated_cost',
            width: 140,
            render: (v) => (v ? `${Number(v).toLocaleString('tr-TR')} ₺` : '—'),
          },
          { title: 'Takım', dataIndex: 'teams_count', width: 80, render: (v) => v ?? 0 },
          {
            title: 'Durum',
            dataIndex: 'is_active',
            width: 100,
            render: (v) => <Tag color={v ? 'success' : 'default'}>{v ? 'Aktif' : 'Pasif'}</Tag>,
          },
          {
            title: 'İşlemler',
            width: 110,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { setModal({ open: true, record: r }); form.setFieldsValue(r); }}
                />
                <Popconfirm
                  title="Proje silinsin mi?"
                  onConfirm={async () => {
                    try {
                      await deleteCompetitionProject(r.id);
                      message.success('Silindi.');
                      fetchRows();
                    } catch (e) {
                      message.warning(apiErrorMessage(e));
                    }
                  }}
                  okText="Sil"
                  cancelText="Vazgeç"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={modal.record ? 'Projeyi Düzenle' : 'Yeni Proje'}
        open={modal.open}
        onOk={submit}
        onCancel={() => { setModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Proje Adı" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="Sera / Atık Dedektörü…" />
          </Form.Item>
          <Form.Item name="tech_details" label="Teknik Detay">
            <Input placeholder="PictoBlox, Arduino, WeDo 2.0…" />
          </Form.Item>
          <Form.Item name="estimated_cost" label="Tahmini Maliyet (₺)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="is_active" label="Durum">
            <Select options={[{ value: true, label: 'Aktif' }, { value: false, label: 'Pasif' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Competitions;
